import { PublicClientApplication, type AccountInfo, type AuthenticationResult } from "@azure/msal-browser";
import { createMsalFlowState, resolveAccountOid, type AuthProvider, type AuthProviderConfig, type MsalFlowDeps } from "@ics/auth-api";
import { login as msalLogin, logout as msalLogout, isAuthenticated as msalIsAuthenticated } from "./popupFlow";
import { loginRedirect as msalLoginRedirect, handleRedirectResponse as msalHandleRedirectResponse } from "./redirectFallback";
import { silentRestoreEntra as msalSilentRestoreEntra } from "./silentRestore";
import { getTokenPopup as msalGetTokenPopup, getTokenSilent as msalGetTokenSilent } from "./tokenFlow";
import { userAccount } from "./sessionState";

/**
 * Entra ID-provider med EGEN MSAL-instans (B2C-paritet, US 2674 skive 2).
 *
 * Instansen bygges fra providerens config — clientId/authority kommer fra
 * `authProviders[type==='entra']`, dvs. SPA-appens app-registrering (Bug 2672).
 * Flyt-logikken (popup-blokkering-deteksjon, redirect-fallback, silent restore
 * med timeout, token-dedupe/-cache) gjenbrukes fra de instans-parameteriserte
 * modulene i `helpers/auth/` (task 2675) via `MsalFlowDeps` — den er flyttet,
 * ikke reimplementert. Token-cache og dedupe (`flowState`) eies per
 * provider-instans; `isPopupBlocked` deles på tvers av providere
 * (nettleser-egenskap, se `msalFlowDeps.ts`).
 *
 * MSAL-optionene er bevisst identiske med den tidligere legacy-singletonens
 * (ingen `knownAuthorities`/`popupBridgeTimeout` — det er B2C-spesifikt);
 * pariteten med B2CProvider gjelder MØNSTERET (egen instans fra config),
 * ikke instans-optionene.
 */
export class EntraProvider implements AuthProvider {
  readonly id: string;
  readonly displayName: string;
  readonly type = "entra" as const;

  private readonly clientId: string;
  private readonly authority: string;
  private readonly scopes: string[];
  private msalInstance: PublicClientApplication | null = null;
  private initPromise: Promise<PublicClientApplication> | null = null;
  private readonly deps: MsalFlowDeps;

  constructor (config: AuthProviderConfig) {
    this.id = config.id;
    this.displayName = config.displayName;
    this.clientId = config.clientId;
    this.authority = config.authority;
    this.scopes = config.scopes;
    this.deps = {
      getInstance: () => this.getInstance(),
      // WI 2448: synkron accessor så login() kan fyre loginPopup i klikk-gesturet.
      getInstanceSync: () => this.msalInstance,
      getTokenRequest: async () => this.buildTokenRequest(),
      // Scopes er konstruktør-gitt → alltid «varme» når de finnes (WI 2448).
      getTokenRequestSync: () => (this.scopes.length > 0 ? { scopes: this.scopes } : null),
      resolveOid: resolveAccountOid,
      // WI 2291: provider-scopet konto-seleksjon ved restore.
      getAuthority: async () => this.authority,
      flowState: createMsalFlowState()
    };
  }

  async login (): Promise<boolean> {
    return await msalLogin(this.deps);
  }

  async loginRedirect (intendedRoute?: string): Promise<void> {
    await msalLoginRedirect(this.deps, intendedRoute);
  }

  async handleRedirectResponse (): Promise<string | null> {
    return await msalHandleRedirectResponse(this.deps);
  }

  async logout (): Promise<void> {
    await msalLogout(this.deps);
  }

  async isAuthenticated (): Promise<boolean> {
    return await msalIsAuthenticated(this.deps);
  }

  async restore (): Promise<boolean> {
    // Warm-restore MÅ aldri gå via `isAuthenticated()` — den faller tilbake
    // til `acquireTokenPopup()` ved silent-feil, og popup blokkeres taust av
    // browser-en under app-boot (ingen user gesture) → MSAL henger ut 60s
    // `popupBridgeTimeout` → `app.mount('#app')` kalles aldri → blank skjerm
    // på dyplenker med utløpt refresh-token (WI 1218 cold-load-bug).
    // `silentRestoreEntra()` er en ren silent-sti med hard Promise.race-timeout.
    try {
      return await msalSilentRestoreEntra(this.deps);
    } catch {
      return false;
    }
  }

  currentAccount (): AccountInfo | null {
    // Leser flyt-eid sesjons-identitet (settes/nullstilles av login/restore/
    // redirect/auth-sjekk) — IKKE `msalInstance.getActiveAccount()`, som kan gi
    // en ikke-tom konto fra stale localStorage-cache etter en FEILET restore og
    // dermed vippe `hasActiveSession()`-kanten (WI 1218/2255).
    return userAccount.value;
  }

  async getTokenSilentOrPopup (): Promise<AuthenticationResult | null> {
    return await msalGetTokenPopup(this.deps);
  }

  async getTokenSilent (): Promise<AuthenticationResult | null> {
    return await msalGetTokenSilent(this.deps);
  }

  private buildTokenRequest (): { scopes: string[] } {
    // Kastes lazy (inne i flytens try/catch) — paritet med legacy
    // scope-valideringen: login/token-kall gir false/null + varsel til
    // brukeren, aldri en reject fra konstruksjonen.
    if (this.scopes.length === 0) {
      throw new Error(`EntraProvider '${this.id}' mangler scopes i konfigurasjonen`);
    }
    return { scopes: this.scopes };
  }

  private async getInstance (): Promise<PublicClientApplication> {
    this.initPromise ??= this.buildInstance();
    return await this.initPromise;
  }

  private async buildInstance (): Promise<PublicClientApplication> {
    if (this.msalInstance) {
      return this.msalInstance;
    }

    // Lazy validering — samme feiltiming som legacy `getMsalConfig` (første
    // flyt-kall), slik at en ufullstendig config gir false/null i flytene.
    if (!this.clientId) {
      throw new Error(`EntraProvider '${this.id}' mangler clientId i konfigurasjonen`);
    }
    if (!this.authority) {
      throw new Error(`EntraProvider '${this.id}' mangler authority i konfigurasjonen`);
    }

    const baseUri = location.origin.endsWith("/") ? location.origin : `${location.origin}/`;
    const instance = new PublicClientApplication({
      auth: {
        clientId: this.clientId,
        authority: this.authority,
        // redirectUri må peke på redirect-bridgen for MSAL v5 COOP
        // (Cross-Origin-Opener-Policy) — samme som B2CProvider.
        redirectUri: `${baseUri}redirect.html`,
        // Lukke-vennlig logout-side (WI 2197): app-roten lastet hele SPA-en i
        // logout-popup-en så den aldri lukket seg. logout.html er en minimal
        // same-origin-side som lar MSAL lukke popup-en.
        postLogoutRedirectUri: `${baseUri}logout.html`
      },
      cache: {
        cacheLocation: "localStorage"
      }
    });
    await instance.initialize();
    this.msalInstance = instance;
    return instance;
  }
}
